/**
 * PlaceAnchorEditor — the name field behind the disclosure arrow on the viewport
 * menu's "Place anchor here" row (ENG-590).
 *
 * An anchor's whole value is its NAME: convorcher hands the names to the
 * character LLM as prompt tokens, so "walk to the door" only works if somebody
 * called it `door`. That makes naming part of the placing gesture rather than a
 * follow-up, and a flyout on the row is where the question is already being
 * asked — the same shape WalkHereMenuEditor uses, for the same reason.
 *
 * The row works with the flyout never opened: `resolvePlaceAnchor` falls back to
 * a free `anchor` / `anchor_2` / … so the gesture needs no typing, and the
 * inspector can rename later. Requiring a name up front would make a
 * three-second placement a form.
 *
 * The field writes into placeAnchorCommand's pending name, not local state: the
 * value is read by `run`, which is not a React component, and the row's LABEL
 * shows it back so what you typed is visible before you commit.
 */
import { useEffect, useState } from 'react'
import { setCommandEditor } from '../lib/viewportCommands'
import { environmentAnchorStore } from '../lib/environmentAnchorStore'
import { labelError, nameError, suggestName } from '../lib/environmentAnchors'
import { pendingAnchorLabel, pendingAnchorName, setPendingAnchorLabel, setPendingAnchorName } from '../lib/placeAnchorCommand'

export default function PlaceAnchorEditor({ run, enabled = true, reason = null }) {
  const { anchors, assetName } = environmentAnchorStore.getState()
  // Prefilled with the same suggestion the row would have used, so opening the
  // flyout and pressing the button is identical to just clicking the row.
  const [text, setText] = useState(() => pendingAnchorName() ?? suggestName('anchor', anchors))
  // The prose the character LLM actually reads. The NAME is a token it
  // addresses ("kitchen_cup"); the LABEL is what makes the token mean something
  // ("the blue cup on the kitchen counter"). Optional, so the gesture still
  // works with no typing at all.
  const [labelText, setLabelText] = useState(() => pendingAnchorLabel())

  // Keep the pending name in step with the field, so the row's label reports
  // what will actually be placed rather than the suggestion it opened with.
  useEffect(() => { setPendingAnchorName(text.trim() || null) }, [text])
  useEffect(() => { setPendingAnchorLabel(labelText) }, [labelText])
  // Placing consumes the pending name (one placement, one name). Clearing it on
  // unmount as well keeps a flyout that was opened and abandoned from naming
  // the NEXT right-click somewhere else in the room.
  useEffect(() => () => { setPendingAnchorName(null); setPendingAnchorLabel('') }, [])

  const err = nameError(text.trim(), anchors)
  const lErr = labelError(labelText)
  const canPlace = enabled && !!run && !err && !lErr

  const submit = () => { if (canPlace) run() }

  return (
    <div data-testid="place-anchor-editor" style={body}>
      <label style={label}>ANCHOR NAME</label>
      <input
        data-testid="place-anchor-name"
        value={text}
        autoFocus
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
        placeholder="door"
        style={input}
      />
      {/* The rule is the SERVER's (environmentAnchors mirrors it), so saying it
          here is the difference between fixing the name now and watching a
          PATCH come back 400 for a name that looked fine. */}
      <div style={err ? errText : hintText}>
        {err ?? 'lowercase, digits and _ — the LLM addresses it by this name'}
      </div>
      <label style={label}>DESCRIPTION (optional)</label>
      <input
        data-testid="place-anchor-label"
        value={labelText}
        onChange={e => setLabelText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
        placeholder="the front door"
        style={input}
      />
      <div style={lErr ? errText : hintText}>
        {lErr ?? 'what the character is told it is walking to'}
      </div>

      <button
        data-testid="place-anchor-submit"
        onClick={submit}
        disabled={!canPlace}
        style={{ ...submitBtn, opacity: canPlace ? 1 : 0.4, cursor: canPlace ? 'pointer' : 'default' }}
        title={enabled ? `Place "${text.trim()}" here on ${assetName ?? 'this environment'}` : (reason ?? 'Unavailable here')}
      >Place anchor</button>
      {!enabled && reason && <div style={errText}>{reason}</div>}
    </div>
  )
}

const body = { padding: '2px 2px 0', width: 232 }
const label = { display: 'block', fontSize: 9, letterSpacing: '0.06em', color: 'var(--m-fg-3)', margin: '6px 0 3px' }
const input = {
  width: '100%', boxSizing: 'border-box', padding: '4px 6px',
  background: 'var(--m-bg)', border: '1px solid var(--m-line)',
  borderRadius: 'var(--m-radius-sm)', color: 'inherit', font: 'inherit',
}
const hintText = { marginTop: 4, fontFamily: 'var(--m-mono)', fontSize: 9.5, lineHeight: 1.3, color: 'var(--m-fg-3)' }
const errText = { ...hintText, color: 'var(--m-warn)' }
const submitBtn = {
  display: 'block', width: '100%', marginTop: 8, padding: '5px 8px',
  background: 'var(--m-accent)', color: 'var(--m-accent-ink)',
  border: '1px solid var(--m-accent)', borderRadius: 'var(--m-radius-sm)',
  font: 'inherit', fontWeight: 600,
}

// The feature contributing its own UI — registered here rather than in
// placeAnchorCommand.js so that lib module stays React-free and node-testable,
// and rather than in ViewportMenu so the menu never names a specific command.
setCommandEditor('placeAnchor', PlaceAnchorEditor)
